import { Box } from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import Appointment, { status } from '@spike/appointment-model';
import { AppointmentsListStatus, fetchAppointmentDailyCountThunk, fetchThunk } from '@spike/appointments-list-action';
import { AppointmentDailyCount, AppointmentListItem } from '@spike/appointments-list-model';
import { CLIENT_SIDE_APPOINTMENTS_PAGE_SIZE } from 'components/Bookings2/List/groomingSort';
import {
    BlockCalendarStatus,
    countRecurrentThunk,
    fetchThunk as fetchBlocksThunk,
    saveThunk
} from '@spike/block-calendar-action';
import { BlockCalendar } from '@spike/block-calendar-model';
import Booking, { BookingWrapper } from '@spike/booking-model';
import { AppointmentsStatus, countRecurrentAppointmentsThunk, fetchAppointmentsThunk } from '@spike/bookings-action';
import { Client as ClientModel } from '@spike/client-model';
import { MarketplaceScheduleType } from '@spike/marketplace-model';
import { BusinessHours, PageResult, createEmptyWeek } from '@spike/model';
import {
    clearAppointmentEditScopeThunk,
    INITIAL_BOOKING_EDIT_SCOPE_REQUEST,
    NewBookingsStatus,
    recurrentRescheduleThunk,
    singleRescheduleThunk,
    updateAppointmentThunk
} from '@spike/new-bookings-action';
import { showError } from '@spike/notifications-action';
import Pet from '@spike/pet-model';
import { Room } from '@spike/room-model';
import { fetchThunk as fetchRoomsThunk, RoomsStatus } from '@spike/rooms-action';
import useNonInitialEffect from '@versiondos/hooks';
import { fetchServicesThunk } from 'actions/services/ServicesActions';
import { fetchOrganizationStaffThunk } from 'actions/staff/StaffActions';
import { BoardingWeekFullCalendar } from 'components/Calendar3/Boarding/WeekFullCalendar';
import { EditGroupSlotFullCalendar } from 'components/Calendar3/FullCalendar/EditGroupSlotFullCalendar';
import { EditSingleSlotFullCalendar } from 'components/Calendar3/FullCalendar/EditSingleSlotFullCalendar';
import BookingActivityLog from 'components/Bookings2/BookingActivityLog';
import Checkout from 'components/Checkout2';
import ConfirmRescheduleRecurringDialog from 'components/CreateBooking/UI/ConfirmRescheduleRecurringDialog';
import OrderDetail from 'components/OrderDetail';
import AppointmentEditScopeDialogContainer from 'components/OrderDetail/UI/RecurringEditScopeDialog/AppointmentEditScopeDialogContainer';
import { OverFullWindow, Spinner } from 'components/UI';
import ConfirmRescheduleDialog from 'components/UI/ConfirmRescheduleDialog';
import CreateBookingDrawer from 'components/UI/CreateBookingDrawer';
import { useApiClientWrapper, useAuth, useQueryParams, useTimeZone } from 'hooks';
import { WeekDay } from 'model';
import Service, { BusinessAreaType, SERVICES_BUSINESS_AREAS } from 'model/Service';
import Staff, { OrganizationStaff, StaffSlot } from 'model/Staff';
import moment, { Moment } from 'moment-timezone';
import Client from 'pages/Client';
import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { ServicesStatus } from 'reducers/services/ServicesState';
import { RootState } from 'store';
import { v4 as uuid } from 'uuid';
import BlockCalendarsWebSockets from 'WebSockets/BlockCalendarsWebSockets';
import PopUpUpdateBlockCalendar from './BlockedTime/PopUpUpdateBlockCalendar';
import CalendarHeader from './CalendarHeader';
import DayFullCalendar from './DayFullCalendar';
import { StaffResource } from './DayFullCalendar/DayFullCalendar';
import { SlotGroup, TempNewBooking } from './FullCalendar/model';
import MonthFullCalendar from './MonthFullCalendar';
import { CalendarView, UNASSIGNED_STAFF_ID } from './types';
import { hasSameDayDuplicateInBusinessArea } from './utils/CalendarUtils';
import {
    getCalendarFilterSelectionByArea,
    normalizeCalendarFilterSelection,
    setCalendarFilterSelectionByArea
} from './utils/calendarFilterStorage';
import WeekFullCalendar from './WeekFullCalendar';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import { StaffStatus } from 'reducers/staff/StaffState';

interface BlockCalendarDraggable {
    blockedTime: BlockCalendar;
    revert: () => void;
}

interface SeriesDrop {
    appointmentId: number;
    at: Moment;
    revert: () => void;
}

const calendarAppointmentStatus = [
    status.REQUESTED,
    status.BOOKED,
    status.CONFIRMED,
    status.CHECKED_IN,
    status.IN_PROGRESS,
    status.READY_FOR_CHECK_OUT,
    status.PAYMENT,
    status.COMPLETED
];

const CALENDAR_SELECTED_STAFF_IDS_BY_AREA_KEY = 'calendarSelectedStaffIdsByArea';
const CALENDAR_SELECTED_ROOM_UNIT_IDS_BY_AREA_KEY = 'calendarSelectedRoomUnitIdsByArea';

const BOARDING_CALENDAR_DATE_RANGE_WEEKS = 8;

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            flexGrow: 1
        },
        body: {
            display: 'flex',
            flexGrow: 1
        },
        rightNewBookingContainer: {
            display: 'flex',
            flexDirection: 'column',
            minWidth: 590,
            overflowY: 'hidden'
        },
        spinnerStyle: {
            height: '100%'
        }
    })
);

interface CalendarProps {
    onDaycareDayClick?: (date: Moment) => void;
}

export const Calendar: FunctionComponent<CalendarProps> = props => {
    const classes = useStyles();

    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const { isUserLoggedInMarketplace, getOrganizationMarketplace, organizationMarketplaces } =
        useOrganizationMarketplaces();

    const auth = useAuth();
    const history = useHistory();
    const queryParams = useQueryParams();
    const sessionTimeZone = useTimeZone();

    const serviceStatus = useSelector<RootState, ServicesStatus>(state => state.services.status);

    const initialToday = moment().tz(sessionTimeZone);

    const parseStoredDateInMarketplaceTimeZone = (dateValue: string | null): Moment | null => {
        if (!dateValue) {
            return null;
        }

        const parsedDate = moment.tz(dateValue, 'YYYY-MM-DD', sessionTimeZone);

        return parsedDate.isValid() ? parsedDate : null;
    };

    const getInitialWeek = () => {
        const storedWeekString = sessionStorage.getItem('calendarWeek');
        if (storedWeekString) {
            try {
                const storedWeek = JSON.parse(storedWeekString);
                const from = parseStoredDateInMarketplaceTimeZone(storedWeek.from);
                const to = parseStoredDateInMarketplaceTimeZone(storedWeek.to);

                if (from && to) {
                    return { from, to };
                }
            } catch {
                // Ignore invalid storage and use fallback week.
            }
        }

        return {
            from: initialToday.clone().startOf('week'),
            to: initialToday.clone().endOf('week')
        };
    };

    const getInitialDate = () => {
        const storedDateString = sessionStorage.getItem('calendarDate');
        const storedDate = parseStoredDateInMarketplaceTimeZone(storedDateString);

        if (storedDate) {
            return storedDate;
        }

        if (storedDateString) {
            // Ignore invalid storage and use fallback date.
        }

        return initialToday.clone();
    };

    const getInitialMonth = () => {
        const storedMonthString = sessionStorage.getItem('calendarMonth');
        const storedMonth = parseStoredDateInMarketplaceTimeZone(storedMonthString);

        if (storedMonth) {
            return storedMonth.clone().startOf('month');
        }

        return initialToday.clone().startOf('month');
    };

    const getInitialBusinessArea = () => {
        const storedBusinessArea = localStorage.getItem('calendarBusinessArea');
        if (storedBusinessArea) {
            return storedBusinessArea as BusinessAreaType;
        }
        return SERVICES_BUSINESS_AREAS.GROOMING;
    };

    const getInitialViewMode = () => {
        const initialBusinessArea = getInitialBusinessArea();
        if (initialBusinessArea === SERVICES_BUSINESS_AREAS.DAYCARE) {
            return CalendarView.Monthly;
        }

        const storedViewMode = localStorage.getItem('calendarViewMode');
        if (storedViewMode) {
            return storedViewMode as CalendarView;
        }
        return CalendarView.Daily;
    };

    const [week, setWeek] = useState(getInitialWeek());
    const [date, setDate] = useState(getInitialDate());
    const [month, setMonth] = useState(getInitialMonth());

    const [viewMode, setViewMode] = useState<CalendarView>(getInitialViewMode());
    const [businessArea, setBusinessArea] = useState<BusinessAreaType>(getInitialBusinessArea());

    const [isSlotsView, setIsSlotsView] = useState(false);

    const [newBooking, setNewBooking] = useState<{ at: Moment; staffId?: number } | undefined>();

    const [tempNewBooking, setTempNewBooking] = useState<TempNewBooking | undefined>();

    const [showBooking, setShowBooking] = useState<{ appointmentId?: number; bookingId?: number; show: boolean }>({
        show: false
    });
    const [orderData, setOrderData] = useState<{ orderId?: number; bookingId?: number; show: boolean }>({
        show: false
    });
    const [orderPanelView, setOrderPanelView] = useState<'booking_detail' | 'activity_log'>('booking_detail');

    const [reschedule, setReschedule] = useState<
        | {
              bookingId: number;
              appointmentId: number;
              petName: string;
              staffId?: number;
              from: Moment;
              to: Moment;
              newEndTime: Moment;
              revert: () => void;
              customerName: string;
              inRecurringSeries?: boolean;
          }
        | undefined
    >();

    const [editSlot, setEditSlot] = useState<
        { slot: StaffSlot; staffId: number; slotElement: HTMLElement } | undefined
    >();
    const [editSlotGroup, setEditSlotGroup] = useState<{ group: SlotGroup; slotElement: HTMLElement } | undefined>();

    const [showBlockCalendar, setShowBlockCalendar] = useState<{ blockId: number; start?: Moment } | undefined>();

    const [reschedulingAppointments, setReschedulingAppointments] = useState<Array<{ id: number; at: Moment }>>([]);
    const [seriesDrop, setSeriesDrop] = useState<SeriesDrop | undefined>();
    const seriesDropRef = useRef<SeriesDrop | undefined>();
    seriesDropRef.current = seriesDrop;

    const appointments = useSelector<RootState, Array<Appointment>>(state => state.appointments.appointments);
    const appointmentDailyCounts = useSelector<RootState, Array<AppointmentDailyCount>>(
        state => state.appointmentsList.dailyCounts
    );
    const boardingAppointments = useSelector<RootState, Array<AppointmentListItem>>(
        state => state.appointmentsList.boardings.result.page
    );
    const appointmentsStatus = useSelector<RootState, AppointmentsStatus>(state => state.appointments.status);
    const appointmentsListStatus = useSelector<RootState, AppointmentsListStatus>(
        state => state.appointmentsList.status
    );

    const blocks = useSelector<RootState, Array<BlockCalendar>>(state => state.blockCalendar.blocks);

    const organizationStaff = useSelector<RootState, Array<OrganizationStaff>>(state => state.staff.organizationStaff);
    const staffStatus = useSelector<RootState, StaffStatus>(state => state.staff.status);

    const rooms = useSelector<RootState, PageResult<Room>>(state => state.rooms.rooms);
    const roomsStatus = useSelector<RootState, RoomsStatus>(state => state.rooms.status);

    const newBookingsStatus = useSelector<RootState, NewBookingsStatus>(state => state.newBookings.status);

    const recurrentBlockCalendar = useSelector<RootState, { blockId: number; remaining: number } | undefined>(
        state => state.blockCalendar.recurrent
    );

    const blockCalendarStatus = useSelector<RootState, BlockCalendarStatus>(state => state.blockCalendar.status);

    const [loading, setLoading] = useState({
        appointments: true,
        blocks: true,
        staff: true,
        rooms: true
    });
    const [selectedRoomUnitIds, setSelectedRoomUnitIds] = useState<Array<number> | undefined>();
    const [selectedStaffIds, setSelectedStaffIds] = useState<Array<number> | undefined>();
    const [selectedLocationId, setSelectedLocationId] = useState<number>(auth.marketplaceId ?? 0);

    const readOnly = useMemo(
        () => !isUserLoggedInMarketplace(selectedLocationId),
        [selectedLocationId, isUserLoggedInMarketplace]
    );
    const marketplace = useMemo(
        () => getOrganizationMarketplace(selectedLocationId),
        [selectedLocationId, getOrganizationMarketplace]
    );
    const calendarTimeZone = marketplace?.schedule.timeZone || sessionTimeZone;
    const calendarToday = moment().tz(calendarTimeZone);
    const showPetsCountBadge = marketplace?.scheduleType === MarketplaceScheduleType.MultiSlots;
    const locationStaff = useMemo(
        () => organizationStaff.filter(s => s.worksAt.some(w => w.marketplaceId === selectedLocationId)),
        [organizationStaff, selectedLocationId]
    );

    const availableStaffIds = useMemo(
        () => [
            UNASSIGNED_STAFF_ID,
            ...locationStaff
                .filter(
                    member =>
                        member.worksAt.some(w => w.marketplaceId === selectedLocationId && w.active) && !member.deleted
                )
                .map(member => member.id)
                .filter((id): id is number => id !== undefined)
        ],
        [locationStaff]
    );

    const availableRoomUnitIds = useMemo(
        () =>
            rooms.page.flatMap(room => room.units.map(unit => unit.id)).filter((id): id is number => id !== undefined),
        [rooms]
    );

    const getRestoredSelectionForArea = useCallback(
        (
            selectedBusinessArea: BusinessAreaType
        ): { staffIds: Array<number> | undefined; roomUnitIds: Array<number> | undefined } => {
            const restoredStaffIds = normalizeCalendarFilterSelection(
                getCalendarFilterSelectionByArea(CALENDAR_SELECTED_STAFF_IDS_BY_AREA_KEY, selectedBusinessArea),
                availableStaffIds
            );

            const restoredRoomUnitIds = normalizeCalendarFilterSelection(
                getCalendarFilterSelectionByArea(CALENDAR_SELECTED_ROOM_UNIT_IDS_BY_AREA_KEY, selectedBusinessArea),
                availableRoomUnitIds
            );

            return {
                staffIds: restoredStaffIds,
                roomUnitIds: restoredRoomUnitIds
            };
        },
        [availableStaffIds, availableRoomUnitIds]
    );

    const restoreSelectionForArea = useCallback(
        (selectedBusinessArea: BusinessAreaType) => {
            const restoredSelection = getRestoredSelectionForArea(selectedBusinessArea);
            setSelectedStaffIds(restoredSelection.staffIds);
            setSelectedRoomUnitIds(restoredSelection.roomUnitIds);
        },
        [getRestoredSelectionForArea]
    );

    const businessAreaChangeHandler = useCallback(
        (nextBusinessArea: BusinessAreaType) => {
            const restoredSelection = getRestoredSelectionForArea(nextBusinessArea);
            setSelectedStaffIds(restoredSelection.staffIds);
            setSelectedRoomUnitIds(restoredSelection.roomUnitIds);
            if (nextBusinessArea === SERVICES_BUSINESS_AREAS.DAYCARE) {
                setViewMode(CalendarView.Monthly);
            }
            setBusinessArea(nextBusinessArea);
        },
        [getRestoredSelectionForArea]
    );

    const staffIdsChangeHandler = useCallback(
        (nextSelectedStaffIds: Array<number> | undefined) => {
            const normalizedSelectedStaffIds = normalizeCalendarFilterSelection(
                nextSelectedStaffIds,
                availableStaffIds
            );
            setSelectedStaffIds(normalizedSelectedStaffIds);
            setCalendarFilterSelectionByArea(
                CALENDAR_SELECTED_STAFF_IDS_BY_AREA_KEY,
                businessArea,
                normalizedSelectedStaffIds,
                availableStaffIds
            );
        },
        [availableStaffIds, businessArea]
    );

    const roomUnitIdsChangeHandler = useCallback(
        (nextSelectedRoomUnitIds: Array<number> | undefined) => {
            const normalizedSelectedRoomUnitIds = normalizeCalendarFilterSelection(
                nextSelectedRoomUnitIds,
                availableRoomUnitIds
            );
            setSelectedRoomUnitIds(normalizedSelectedRoomUnitIds);
            setCalendarFilterSelectionByArea(
                CALENDAR_SELECTED_ROOM_UNIT_IDS_BY_AREA_KEY,
                businessArea,
                normalizedSelectedRoomUnitIds,
                availableRoomUnitIds
            );
        },
        [availableRoomUnitIds, businessArea]
    );

    const [showNewClient, setShowNewClient] = useState(false);
    const [showClient, setShowClient] = useState(false);
    const [isNewBooking, setIsNewBooking] = useState(false);
    const [customerID, setCustomerID] = useState<number | undefined>(undefined);
    const [petID, setPetID] = useState<number | undefined>(undefined);
    const [petAdded, setPetAdded] = useState(false);
    const [clientAdded, setClientAdded] = useState(false);
    const [rescheduleBooking, setRescheduleBooking] = useState<BookingWrapper | undefined>(undefined);
    const [recurring, setRecurring] = useState(false);

    const [rescheduleFromDetail, setRescheduleFromDetail] = useState(false);
    const [rebookBooking, setRebookBooking] = useState<Booking | undefined>(undefined);
    const [savingBlockedTime, setSavingBlockedTime] = useState(false);
    const [showUpdateConfirmation, setShowUpdateConfirmation] = useState(false);
    const [blockedTime, setBlockedTime] = useState<BlockCalendarDraggable | undefined>(undefined);
    const [dailyView, setDailyView] = useState(false);
    const [checkoutData, setCheckoutData] = useState<
        { orderId?: number; bookingId: number; back: boolean; hide: boolean } | undefined
    >();

    const changeLocationIdHandler = useCallback((newLocationId: number) => {
        setSelectedLocationId(newLocationId);
        setSelectedStaffIds(undefined);
    }, []);

    const dateChangeHandler = useCallback((newDate: Moment) => {
        sessionStorage.setItem('calendarDate', newDate.format('YYYY-MM-DD'));
        setDate(newDate);
    }, []);

    const weekChangeHandler = useCallback((newWeek: { from: Moment; to: Moment }) => {
        sessionStorage.setItem(
            'calendarWeek',
            JSON.stringify({
                from: newWeek.from.format('YYYY-MM-DD'),
                to: newWeek.to.format('YYYY-MM-DD')
            })
        );
        setWeek(newWeek);
    }, []);

    const monthChangeHandler = useCallback((newMonth: Moment) => {
        sessionStorage.setItem('calendarMonth', newMonth.format('YYYY-MM-DD'));
        setMonth(newMonth);
    }, []);

    useEffect(() => {
        localStorage.setItem('calendarViewMode', viewMode);
    }, [viewMode]);

    useEffect(() => {
        localStorage.setItem('calendarBusinessArea', businessArea);
    }, [businessArea]);

    useEffect(() => {
        restoreSelectionForArea(businessArea);
    }, [businessArea, restoreSelectionForArea]);

    const multipleBookingIds = useMemo(() => {
        return Array.from(
            appointments
                .reduce((map, appointment) => {
                    const bookingId = appointment.bookingId;
                    const appointmentsCount = map.get(bookingId) ?? 0;
                    map.set(bookingId, appointmentsCount + 1);
                    return map;
                }, new Map<number, number>())
                .entries()
        )
            .filter(entry => entry[1] > 1)
            .map(entry => entry[0]);
    }, [appointments]);

    const calendarAppointments = useMemo(
        () =>
            appointments.filter(
                appointment =>
                    selectedStaffIds === undefined || selectedStaffIds.includes(appointment.services[0].staff?.id ?? 0)
            ),
        [appointments, selectedStaffIds]
    );

    const calendarBlocks = useMemo(
        () => blocks.filter(block => selectedStaffIds === undefined || selectedStaffIds.includes(block.staff?.id ?? 0)),
        [blocks, selectedStaffIds]
    );

    const calendarStaff: Array<Staff> = useMemo(
        () =>
            locationStaff
                .filter(
                    member =>
                        (member.worksAt.find(w => w.marketplaceId === selectedLocationId)?.active ?? false) &&
                        !member.deleted
                )
                .map(member => ({
                    id: member.id,
                    uuid: member.uuid,
                    person: {
                        firstName: member.person.firstName,
                        lastName: member.person.lastName,
                        avatar: member.person.avatar,
                        address: '',
                        phoneNumber: '',
                        email: ''
                    },
                    businessAreas: [],
                    emergencyContact: {
                        firstName: '',
                        lastName: '',
                        email: '',
                        phoneNumber: ''
                    },
                    services: [],
                    notifications: {
                        phone: false,
                        email: false
                    },
                    schedule: member.scheduleByMarketplace.get(selectedLocationId) ?? {
                        default: createEmptyWeek(),
                        customDays: []
                    },
                    active: member.worksAt.find(work => work.marketplaceId === selectedLocationId)?.active ?? false,
                    deleted: member.deleted,
                    destroyable: false,
                    worksAt: [...member.worksAt],
                    last_login: null,
                    slots: member.slotsByMarketplace.get(selectedLocationId) ?? {
                        default: [],
                        customDays: []
                    },
                    roomIds: [],
                    staffColor: member.staffColor ?? null
                }))
                .sort((staffA, staffB) =>
                    `${staffA.person.firstName} ${staffA.person.lastName}`
                        .toLowerCase()
                        .localeCompare(`${staffB.person.firstName} ${staffB.person.lastName}`.toLowerCase())
                ),
        [locationStaff, selectedLocationId]
    );

    const staffResource: Array<StaffResource> = useMemo(
        () =>
            calendarStaff
                .filter(member => selectedStaffIds === undefined || selectedStaffIds.includes(member.id ?? 0))
                .map(member => ({
                    id: member.id ?? 0,
                    firstName: member.person.firstName,
                    lastName: member.person.lastName,
                    avatar: member.person.avatar,
                    schedule: member.schedule,
                    slots: member.slots,
                    staffColor: member.staffColor ?? null
                })),
        [calendarStaff, selectedStaffIds]
    );

    const blockedTimeStaffOptions = calendarStaff;

    const previousCalendarTimeZoneRef = React.useRef(calendarTimeZone);

    useEffect(() => {
        if (previousCalendarTimeZoneRef.current === calendarTimeZone) {
            return;
        }

        previousCalendarTimeZoneRef.current = calendarTimeZone;

        dateChangeHandler(moment.tz(date.format('YYYY-MM-DD'), 'YYYY-MM-DD', calendarTimeZone));
        monthChangeHandler(moment.tz(month.format('YYYY-MM-DD'), 'YYYY-MM-DD', calendarTimeZone).startOf('month'));
        weekChangeHandler({
            from: moment.tz(week.from.format('YYYY-MM-DD'), 'YYYY-MM-DD', calendarTimeZone),
            to: moment.tz(week.to.format('YYYY-MM-DD'), 'YYYY-MM-DD', calendarTimeZone)
        });
    }, [calendarTimeZone, date, month, week, dateChangeHandler, monthChangeHandler, weekChangeHandler]);

    useEffect(() => {
        // Date param must be YYYY-MM-DD
        const dateParam = queryParams.get('date');
        const calendarTypeParam = queryParams.get('calendar');
        const bookingIdParam = queryParams.get('bookingId');
        const orderIdParam = queryParams.get('orderId');

        if (calendarTypeParam) {
            setViewMode(calendarTypeParam as CalendarView);
        }

        if (dateParam) {
            const queryDate = moment.tz(dateParam, 'YYYY-MM-DD', calendarTimeZone);

            if (queryDate.isValid() && !date.isSame(queryDate, 'day')) {
                dateChangeHandler(queryDate);
            }
        }

        if (bookingIdParam && orderIdParam) {
            setOrderData({
                bookingId: Number(bookingIdParam),
                orderId: Number(orderIdParam),
                show: true
            });
        }

        // Clear query params
        if (calendarTypeParam || dateParam || bookingIdParam) {
            history.replace({
                search: ''
            });
        }
    }, [queryParams, date, dateChangeHandler, history, calendarTimeZone]);

    useEffect(() => {
        dispatch(fetchServicesThunk());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        dispatch(fetchRoomsThunk(apiClientWrapper, 1, 2000, [selectedLocationId]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLocationId]);

    useNonInitialEffect(() => {
        switch (roomsStatus) {
            case RoomsStatus.FetchSuccess:
            case RoomsStatus.Error:
                setLoading(prev => ({ ...prev, rooms: false }));
                break;
        }
    }, [roomsStatus]);

    useEffect(() => {
        dispatch(fetchOrganizationStaffThunk());
    }, []);

    useNonInitialEffect(() => {
        switch (staffStatus) {
            case StaffStatus.OrganizationStaffFetchSuccess:
            case StaffStatus.Error:
                setLoading(prev => ({ ...prev, staff: false }));
                break;
        }
    }, [staffStatus]);

    useEffect(() => {
        if (viewMode === CalendarView.Monthly) {
            setLoading(prev => ({ ...prev, appointments: true }));

            if (businessArea !== SERVICES_BUSINESS_AREAS.BOARDING) {
                dispatch(
                    fetchAppointmentDailyCountThunk(
                        apiClientWrapper,
                        selectedLocationId,
                        month.clone().format('YYYY-MM-DD'),
                        month.clone().endOf('month').format('YYYY-MM-DD'),
                        calendarAppointmentStatus,
                        businessArea
                    )
                );
            } else {
                dispatch(
                    fetchAppointmentsThunk(
                        apiClientWrapper,
                        [selectedLocationId],
                        calendarAppointmentStatus,
                        month.clone().format('YYYY-MM-DD'),
                        month.clone().endOf('month').format('YYYY-MM-DD')
                    )
                );
            }
        } else {
            if (week) {
                setLoading(prev => ({
                    ...prev,
                    appointments: true,
                    blocks: true
                }));

                if (businessArea !== SERVICES_BUSINESS_AREAS.BOARDING) {
                    dispatch(
                        fetchAppointmentsThunk(
                            apiClientWrapper,
                            [selectedLocationId],
                            calendarAppointmentStatus,
                            viewMode === CalendarView.Weekly
                                ? week.from.clone().format('YYYY-MM-DD')
                                : date.clone().format('YYYY-MM-DD'),
                            viewMode === CalendarView.Weekly
                                ? week.to.clone().format('YYYY-MM-DD')
                                : date.clone().format('YYYY-MM-DD')
                        )
                    );
                } else {
                    const boardingFrom = week.from.clone().subtract(BOARDING_CALENDAR_DATE_RANGE_WEEKS, 'weeks');
                    const boardingTo = week.to.clone().add(BOARDING_CALENDAR_DATE_RANGE_WEEKS, 'weeks');
                    dispatch(
                        fetchThunk(
                            apiClientWrapper,
                            SERVICES_BUSINESS_AREAS.BOARDING,
                            SERVICES_BUSINESS_AREAS.BOARDING,
                            [selectedLocationId],
                            'boarding_all',
                            boardingFrom.format('YYYY-MM-DD'),
                            boardingTo.format('YYYY-MM-DD'),
                            'Week',
                            calendarAppointmentStatus,
                            selectedRoomUnitIds ?? [],
                            ['arrivals', 'checked_in', 'checked_out', 'departures', 'all'],
                            [],
                            null,
                            null,
                            null,
                            '',
                            1,
                            CLIENT_SIDE_APPOINTMENTS_PAGE_SIZE
                        )
                    );
                }

                dispatch(
                    fetchBlocksThunk(
                        apiClientWrapper,
                        viewMode === CalendarView.Weekly ? week.from.clone() : date.clone(),
                        viewMode === CalendarView.Weekly ? week.to.clone() : date.clone(),
                        marketplace?.schedule.id ?? 0
                    )
                );
            }
        }
    }, [month, week, date, viewMode, businessArea, selectedRoomUnitIds, selectedLocationId]);

    const refetchVisibleAppointments = () => {
        if (!week || viewMode === CalendarView.Monthly || businessArea === SERVICES_BUSINESS_AREAS.BOARDING) {
            return;
        }

        dispatch(
            fetchAppointmentsThunk(
                apiClientWrapper,
                [selectedLocationId],
                calendarAppointmentStatus,
                viewMode === CalendarView.Weekly
                    ? week.from.clone().format('YYYY-MM-DD')
                    : date.clone().format('YYYY-MM-DD'),
                viewMode === CalendarView.Weekly
                    ? week.to.clone().format('YYYY-MM-DD')
                    : date.clone().format('YYYY-MM-DD')
            )
        );
    };

    useNonInitialEffect(() => {
        switch (appointmentsStatus) {
            case AppointmentsStatus.FetchSuccess:
            case AppointmentsStatus.Error:
                setLoading(prev => ({ ...prev, appointments: false, blocks: false }));
        }
    }, [appointmentsStatus]);

    useNonInitialEffect(() => {
        switch (appointmentsListStatus) {
            case AppointmentsListStatus.DailyCountFetchSuccess:
            case AppointmentsListStatus.GroomingsError:
                if (viewMode === CalendarView.Monthly) {
                    setLoading(prev => ({ ...prev, appointments: false, blocks: false }));
                }
                break;
            case AppointmentsListStatus.BoardingsFetchSuccess:
            case AppointmentsListStatus.BoardingsError:
                setLoading(prev => ({ ...prev, appointments: false, blocks: false }));
                break;
        }
    }, [appointmentsListStatus, viewMode]);

    useEffect(() => {
        switch (serviceStatus) {
            case ServicesStatus.Fetching:
                setLoading(prev => ({ ...prev, appointments: true, blocks: true }));
                break;
            case ServicesStatus.FetchSuccess:
                setLoading(prev => ({ ...prev, appointments: false, blocks: false }));
                break;
            case ServicesStatus.Error:
                setLoading(prev => ({ ...prev, appointments: false, blocks: false }));
                break;
        }
    }, [serviceStatus]);

    useNonInitialEffect(() => {
        switch (newBookingsStatus) {
            case NewBookingsStatus.SingleRescheduleSuccess:
                setReschedule(undefined);
                closeAppointmentModal();
                setReschedulingAppointments(prev =>
                    prev.filter(rescheduledApp => {
                        const appointment = appointments.find(app => app.id === rescheduledApp.id);
                        return !appointment?.duration.from.isSame(rescheduledApp.at);
                    })
                );
                break;
            case NewBookingsStatus.RecurrentRescheduleSuccess:
                setReschedule(undefined);
                closeAppointmentModal();
                setRecurring(false);
                break;
            case NewBookingsStatus.Error:
                reschedule && reschedule.revert();
                setRecurring(false);
                setReschedule(undefined);
                setReschedulingAppointments([]);
        }
    }, [newBookingsStatus]);

    useNonInitialEffect(() => {
        if (!seriesDrop) {
            return;
        }

        switch (newBookingsStatus) {
            case NewBookingsStatus.UpdatingAppointment:
                setReschedulingAppointments(prev =>
                    prev.some(rescheduling => rescheduling.id === seriesDrop.appointmentId)
                        ? prev
                        : [...prev, { id: seriesDrop.appointmentId, at: seriesDrop.at.clone() }]
                );
                break;
            case NewBookingsStatus.UpdateAppointmentScopeRequired:
                setReschedulingAppointments(prev =>
                    prev.filter(rescheduling => rescheduling.id !== seriesDrop.appointmentId)
                );
                break;
            case NewBookingsStatus.UpdateAppointmentSuccess:
                setSeriesDrop(undefined);
                setReschedulingAppointments(prev =>
                    prev.filter(rescheduling => rescheduling.id !== seriesDrop.appointmentId)
                );
                refetchVisibleAppointments();
                break;
            case NewBookingsStatus.UpdateAppointmentScopeCancelled:
            case NewBookingsStatus.Error:
                seriesDrop.revert();
                setSeriesDrop(undefined);
                setReschedulingAppointments(prev =>
                    prev.filter(rescheduling => rescheduling.id !== seriesDrop.appointmentId)
                );
                break;
        }
    }, [newBookingsStatus]);

    useEffect(
        () => () => {
            seriesDropRef.current && dispatch(clearAppointmentEditScopeThunk());
        },
        []
    );

    useNonInitialEffect(() => {
        switch (blockCalendarStatus) {
            case BlockCalendarStatus.CountRecurrentSuccess:
                savingBlockedTime &&
                    (recurrentBlockCalendar?.remaining ?? 1) === 1 &&
                    updateBlockedTime(blockedTime?.blockedTime!);
                savingBlockedTime && (recurrentBlockCalendar?.remaining ?? 1) > 1 && setShowUpdateConfirmation(true);
                break;
            case BlockCalendarStatus.SaveSuccess:
                setSavingBlockedTime(false);
                setShowUpdateConfirmation(false);
                setBlockedTime(undefined);
                break;
            case BlockCalendarStatus.Error:
                setSavingBlockedTime(false);
                setShowUpdateConfirmation(false);
                setBlockedTime(undefined);
                blockedTime?.revert();
        }
    }, [blockCalendarStatus]);

    useNonInitialEffect(() => {
        if (dailyView) {
            const dateToView = calendarAppointments[0]?.duration?.from?.clone();
            if (dateToView) {
                setDailyView(false);
                dateChangeHandler(dateToView.clone());
                setTempNewBooking({
                    at: dateToView.clone(),
                    durationMinutes: 15,
                    staffId: undefined
                });
            }
        }
    }, [calendarAppointments]);

    const bookHandler = (at: Moment, staffId?: number) => {
        setNewBooking({ at, staffId });
    };

    const showOrderHandler = (appointmentId: number) => {
        const appointment = appointments.find(appointment => appointment.id === appointmentId);
        const boardingAppointment = boardingAppointments.find(
            appointment => appointment.appointmentId === appointmentId
        );

        const orderId = appointment?.orderId || boardingAppointment?.orderId;
        const bookingId = appointment?.bookingId || boardingAppointment?.bookingId;

        if (bookingId && orderId) {
            setOrderPanelView('booking_detail');
            setOrderData(prev => ({
                ...prev,
                orderId: orderId,
                bookingId: bookingId,
                show: true
            }));
        }
    };

    const closeOrderHandler = () => {
        setOrderPanelView('booking_detail');
        setOrderData(prev => ({ ...prev, show: false }));
    };

    const closeAppointmentModal = () => {
        setShowBooking(prev => ({ ...prev, show: false }));
    };

    const showBlockCalendarHandler = (blockId: number) => {
        const block = blocks.find(block => block.id === blockId);
        setShowBlockCalendar({ blockId, start: block?.start.clone() });
    };

    const rescheduleHandler = (appointmentId: number, at: Moment, revert: () => void, staffId?: number) => {
        const appointment = appointments.find(appointment => appointment.id === appointmentId);
        if (!appointment) {
            revert();
            return;
        }

        const hasDuplicateOnTargetDay = hasSameDayDuplicateInBusinessArea(
            appointmentId,
            at,
            businessArea,
            appointments,
            calendarTimeZone
        );
        if (hasDuplicateOnTargetDay) {
            revert();
            dispatch(showError(`This pet already has an appointment on this day.`));
            return;
        }

        const durationInMinutes = appointment.duration.to.clone().diff(appointment.duration.from.clone(), 'minutes');
        const newEndTime = at.clone().add(durationInMinutes, 'minutes');
        const inRecurringSeries = appointment.recurringSeriesId != null;

        if (appointment.duration.from.isSame(at) && inRecurringSeries) {
            sendSeriesReschedule(appointmentId, at, newEndTime, staffId, undefined, revert);
        } else if (appointment.duration.from.isSame(at)) {
            setReschedulingAppointments(prev => [...prev, { id: appointmentId, at: at.clone() }]);
            dispatch(
                singleRescheduleThunk(
                    apiClientWrapper,
                    appointment.bookingId,
                    appointmentId,
                    at,
                    staffId,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    newEndTime,
                    undefined,
                    true,
                    undefined,
                    undefined,
                    undefined
                )
            );
        } else {
            const legacyRecurring = !inRecurringSeries && !!appointment.recurringUuid;
            setRecurring(legacyRecurring);
            if (legacyRecurring) {
                dispatch(countRecurrentAppointmentsThunk(apiClientWrapper, appointmentId));
            }
            setReschedule({
                appointmentId,
                bookingId: appointment.bookingId,
                petName: appointment.pet.name,
                from: appointment.duration.from,
                to: at,
                newEndTime: newEndTime,
                revert,
                staffId,
                customerName: appointment.customer.firstName + ' ' + appointment.customer.lastName,
                inRecurringSeries
            });
        }
    };

    const sendSeriesReschedule = (
        appointmentId: number,
        at: Moment,
        newEndTime: Moment,
        staffId: number | undefined,
        sendNotification: boolean | undefined,
        revert: () => void
    ) => {
        setSeriesDrop({ appointmentId, at: at.clone(), revert });
        setReschedulingAppointments(prev => [...prev, { id: appointmentId, at: at.clone() }]);
        dispatch(
            updateAppointmentThunk(
                apiClientWrapper,
                appointmentId,
                { type: 'reschedule', bookedAt: at, bookingEndAt: newEndTime, staffId },
                sendNotification,
                'calendar',
                INITIAL_BOOKING_EDIT_SCOPE_REQUEST
            )
        );
    };

    const rescheduleBlockedHandler = (blockedData: BlockCalendar, revert: () => void) => {
        setSavingBlockedTime(true);
        dispatch(countRecurrentThunk(apiClientWrapper, blockedData.id!));
        setBlockedTime({ blockedTime: blockedData, revert });
    };

    const updateBlockedTime = (blockData: BlockCalendar) => {
        dispatch(saveThunk(apiClientWrapper, blockData));
    };

    const cancelRescheduleHandler = () => {
        reschedule && reschedule.revert();
        setReschedule(undefined);
        setRecurring(false);
    };

    const confirmRescheduleHandler = (check?: boolean, value?: string) => {
        if (reschedule?.inRecurringSeries) {
            sendSeriesReschedule(
                reschedule.appointmentId,
                reschedule.to,
                reschedule.newEndTime,
                reschedule.staffId,
                check,
                reschedule.revert
            );
            setReschedule(undefined);
        } else if (reschedule) {
            if (recurring) {
                if (value === 'only') {
                    dispatch(
                        singleRescheduleThunk(
                            apiClientWrapper,
                            reschedule.bookingId,
                            reschedule.appointmentId,
                            reschedule.to,
                            reschedule.staffId,
                            undefined,
                            undefined,
                            check,
                            undefined,
                            reschedule.newEndTime,
                            undefined,
                            true,
                            undefined,
                            undefined,
                            undefined
                        )
                    );
                } else {
                    dispatch(
                        recurrentRescheduleThunk(
                            apiClientWrapper,
                            reschedule.appointmentId,
                            reschedule.to,
                            reschedule.staffId,
                            undefined,
                            undefined,
                            undefined,
                            check,
                            undefined,
                            reschedule.newEndTime,
                            undefined
                        )
                    );
                }
            } else {
                dispatch(
                    singleRescheduleThunk(
                        apiClientWrapper,
                        reschedule.bookingId,
                        reschedule.appointmentId,
                        reschedule.to,
                        reschedule.staffId,
                        undefined,
                        undefined,
                        check,
                        undefined,
                        reschedule.newEndTime,
                        undefined,
                        true,
                        undefined,
                        undefined,
                        undefined
                    )
                );
            }
        }
    };

    const closeNewBooking = () => {
        setNewBooking(undefined);
        setShowBlockCalendar(undefined);
        setTempNewBooking(undefined);
        setIsNewBooking(false);
        setCustomerID(undefined);
        setPetID(undefined);
        setClientAdded(false);
        setPetAdded(false);
    };

    const handleRemoveClient = () => {
        setCustomerID(undefined);
        setClientAdded(false);
        setPetID(undefined);
        setPetAdded(false);
    };

    const handlerChangeToday = () => {
        if (viewMode === CalendarView.Weekly) {
            weekChangeHandler({
                from: calendarToday.clone().startOf('week'),
                to: calendarToday.clone().endOf('week')
            });
        } else if (viewMode === CalendarView.Monthly) {
            monthChangeHandler(calendarToday.clone().startOf('month'));
        } else {
            dateChangeHandler(calendarToday.clone());
        }
    };

    const changeBookingTimeHandler = (
        at: Moment | undefined,
        durationMinutes: number | undefined,
        staffId: number | undefined,
        show: boolean
    ) => {
        const dateFormat = 'YYYYMMDD';

        if (at && viewMode === CalendarView.Weekly && at.clone().week() !== week.from.clone().week()) {
            setLoading(prev => ({ ...prev, appointments: true }));
            weekChangeHandler({
                from: at.clone().startOf('week'),
                to: at.clone().endOf('week')
            });
        }

        if (at && viewMode === CalendarView.Daily && date.format(dateFormat) !== at.format(dateFormat)) {
            setLoading(prev => ({ ...prev, appointments: true }));
            dateChangeHandler(at.clone());
        }

        if (show && at) {
            setTempNewBooking({
                at: at.clone(),
                durationMinutes: durationMinutes ?? 15,
                staffId: staffId ?? newBooking?.staffId
            });
        } else {
            setTempNewBooking(undefined);
        }
    };

    const addNewClient = () => {
        setShowNewClient(true);
    };

    const backClientHandler = () => {
        setCheckoutData(prev => (prev === undefined ? undefined : { ...prev, hide: false }));
        setShowNewClient(false);
        setShowClient(false);
        setIsNewBooking(false);
        setCustomerID(undefined);
    };

    const deleteClientHandler = () => {
        setShowNewClient(false);
        setShowClient(false);
        setIsNewBooking(false);
        setCustomerID(undefined);
    };

    const clientSaveHandler = (client: ClientModel) => {
        if (checkoutData === undefined) {
            setCustomerID(client.id);
            setIsNewBooking(false);
            setClientAdded(true);
        } else {
            setShowClient(false);
            setCustomerID(undefined);
            setCheckoutData(prev => (prev === undefined ? undefined : { ...prev, hide: false }));
        }
    };

    const handlerCreateClient = (pet: Pet) => {
        setCustomerID(pet.clientId);
        setPetID(pet.id);
        setShowNewClient(false);
        setShowClient(false);
        setIsNewBooking(false);
        setPetAdded(true);
    };

    const handlerBooked = () => {
        closeNewBooking();
    };

    const addNewPet = (clientID: number) => {
        setCustomerID(clientID);
        setShowNewClient(true);
        setIsNewBooking(true);
    };

    const handlerRescheduleFromDetail = (rescheduleBook: BookingWrapper) => {
        setRescheduleFromDetail(true);
        setRescheduleBooking(rescheduleBook);
    };

    const handleBackRescheduleFromDetail = () => {
        setRescheduleFromDetail(false);
        setRescheduleBooking(undefined);
        setRebookBooking(undefined);
    };

    const handleShowClient = (clientId: number) => {
        setCheckoutData(prev => (prev === undefined ? undefined : { ...prev, hide: true }));
        setShowClient(true);
        setCustomerID(clientId);
    };

    const dailyViewHandler = (at: Moment, staffId?: number) => {
        setViewMode(CalendarView.Daily);
        setDailyView(true);
        dateChangeHandler(at.clone());
        setTempNewBooking({
            at: at.clone(),
            durationMinutes: 15,
            staffId: undefined
        });
    };

    const monthDayClickHandler = (at: Moment) => {
        if (businessArea === SERVICES_BUSINESS_AREAS.DAYCARE && props.onDaycareDayClick) {
            props.onDaycareDayClick(at);
            return;
        }

        dailyViewHandler(at);
    };

    const checkoutHandler = (bookingId: number, orderId?: number, enableBack?: boolean) => {
        setOrderPanelView('booking_detail');
        setOrderData(prev => ({ ...prev, show: false }));
        setCheckoutData({ orderId, bookingId, back: enableBack ?? false, hide: false });
    };

    const checkoutCloseHandler = () => {
        setCheckoutData(undefined);
    };

    const checkoutBackHandler = () => {
        setCheckoutData(undefined);
        if (orderData.bookingId) {
            setOrderData(prev => ({ ...prev, show: true }));
        }
    };

    const handleCalendarScroll = () => {
        setEditSlot(undefined);
    };

    const createBooking = (
        <CreateBookingDrawer
            at={newBooking?.at ?? showBlockCalendar?.start}
            marketplaceId={selectedLocationId}
            staffId={newBooking?.staffId}
            blockedTimeStaffOptions={blockedTimeStaffOptions}
            parentID={customerID}
            petID={petID}
            clientAdded={clientAdded}
            petAdded={petAdded}
            showTabs={true}
            blockCalendarId={showBlockCalendar?.blockId}
            onClose={closeNewBooking}
            onBooked={handlerBooked}
            onChangeTime={changeBookingTimeHandler}
            onStartBooking={handlerBooked}
            onAddClient={addNewClient}
            onAddPet={addNewPet}
            onRemoveClient={handleRemoveClient}
            blockedEdit={showBlockCalendar?.blockId ? true : false}
        />
    );

    const rescheduleBookingView = (
        <CreateBookingDrawer
            detailView={true}
            bookingReschedule={rescheduleBooking}
            appointmentId={showBooking.appointmentId}
            onClose={handleBackRescheduleFromDetail}
            onBooked={handleBackRescheduleFromDetail}
        />
    );

    const orderDetailView = (
        <OrderDetail
            orderId={orderData.orderId!}
            bookingId={orderData.bookingId!}
            onClose={closeOrderHandler}
            onCheckout={(bookingId, orderId) => checkoutHandler(bookingId, orderId, true)}
            onRebookOrder={closeOrderHandler}
            onRebookService={closeOrderHandler}
            onShowActivityLog={() => setOrderPanelView('activity_log')}
        />
    );

    const checkout = (
        <Checkout
            orderId={checkoutData?.orderId ?? 0}
            bookingId={checkoutData?.bookingId ?? 0}
            onClose={checkoutCloseHandler}
            onViewBooking={checkoutData?.back ? checkoutBackHandler : undefined}
            onPayByLinkSent={checkoutCloseHandler}
            onBack={checkoutData?.back ? checkoutBackHandler : undefined}
            onEditClient={handleShowClient}
            hide={checkoutData?.hide ?? false}
        />
    );

    const newClient = (
        <OverFullWindow onClose={backClientHandler}>
            <Client
                hideHeader={true}
                clientId={customerID}
                isNewBooking={isNewBooking}
                onClose={backClientHandler}
                onClientSaved={clientSaveHandler}
                onPetSaved={handlerCreateClient}
                onDelete={deleteClientHandler}
            />
        </OverFullWindow>
    );

    const reBook = (
        <CreateBookingDrawer
            bookingRebook={rebookBooking}
            detailView={true}
            petID={rebookBooking?.appointments[0].pet.id}
            parentID={rebookBooking?.customer.id}
            appointmentId={rebookBooking?.appointments[0].id}
            showTabs={false}
            onClose={handleBackRescheduleFromDetail}
            onBooked={handleBackRescheduleFromDetail}
        />
    );

    const updateConfirmation = (
        <PopUpUpdateBlockCalendar
            onClose={() => {
                setShowUpdateConfirmation(false);
                setSavingBlockedTime(false);
                blockedTime?.revert();
            }}
            onConfirm={recurrent => updateBlockedTime(blockedTime?.blockedTime!)}
        />
    );

    // Get Marketplace Day Schedule
    const getMarketplaceDaySchedule = (date: Moment): BusinessHours | null => {
        const dayName = date.format('dddd').toLowerCase() as WeekDay;
        return marketplace?.schedule?.week?.[dayName] ?? null;
    };

    const createTimeMoment = (date: Moment, timeString: string): Moment => {
        const [hour, minute] = timeString.split(':').map(Number);
        return date.clone().tz(calendarTimeZone).startOf('day').hour(hour).minute(minute);
    };

    // Find first working day of a given week
    const getFirstWorkingDayOfWeek = useMemo(() => {
        return (weekStart: Moment): Moment => {
            // weekStart comes from week.from which is already the start of the week
            // Iterate through the 7 days of that week
            for (let dayOffset = 0; dayOffset < 7; dayOffset++) {
                const date = weekStart.clone().add(dayOffset, 'days');

                if (getMarketplaceDaySchedule(date)) {
                    return date;
                }
            }

            // No working days found - fallback to the start of the week
            return weekStart.clone();
        };
    }, [marketplace?.schedule?.week]);

    // Get opening hour for a specific date
    const getFirstWorkingHourOfDay = useMemo(() => {
        return (date: Moment): Moment => {
            const todaySchedule = getMarketplaceDaySchedule(date);

            if (todaySchedule?.open) {
                return createTimeMoment(date, todaySchedule.open);
            }

            // Default to 9:00 AM if no schedule
            return date.clone().tz(calendarTimeZone).startOf('day').hour(9).minute(0);
        };
    }, [marketplace?.schedule?.week, calendarTimeZone]);

    // Check if current time is within business hours
    const isCurrentTimeInBusinessHours = (currentTime: Moment): boolean => {
        const daySchedule = getMarketplaceDaySchedule(currentTime);

        if (!daySchedule?.open || !daySchedule?.close) {
            return false;
        }

        const openTime = createTimeMoment(currentTime, daySchedule.open);
        const closeTime = createTimeMoment(currentTime, daySchedule.close);

        return currentTime.isBetween(openTime, closeTime, undefined, '[)');
    };

    // Helper: Find next working day from a given date within the same week
    const getNextWorkingDayInWeek = (startDate: Moment): Moment | null => {
        for (let daysAhead = 1; daysAhead < 7; daysAhead++) {
            const nextDate = startDate.clone().add(daysAhead, 'days');

            if (nextDate.isSame(week.from, 'week') && getMarketplaceDaySchedule(nextDate)) {
                return nextDate;
            }
        }
        return null;
    };

    // Helper: Get opening time for today if before opening hours
    const getTodayOpeningTimeIfEarly = (now: Moment): Moment | null => {
        const todaySchedule = getMarketplaceDaySchedule(now);

        if (todaySchedule?.open) {
            const openingTime = createTimeMoment(now, todaySchedule.open);

            return now.isBefore(openingTime) ? openingTime : null;
        }
        return null;
    };

    const getDefaultDateTimeForView = (): Moment => {
        // Daily view: use selected date
        if (viewMode === CalendarView.Daily) {
            return date.clone();
        }

        // Weekly view: smart date/time selection
        if (viewMode === CalendarView.Weekly) {
            const now = calendarToday.clone();

            // Different week: use first working day
            if (!week.from.isSame(now, 'week')) {
                const firstWorkingDay = getFirstWorkingDayOfWeek(week.from);
                return getFirstWorkingHourOfDay(firstWorkingDay);
            }

            const dayName = now.format('dddd').toLowerCase() as WeekDay;
            const isTodayWorking = !!marketplace?.schedule?.week?.[dayName];

            // Today is working day + in business hours → use now
            if (isTodayWorking && isCurrentTimeInBusinessHours(now)) {
                return now.clone();
            }

            // Today is working day + before opening → use opening time today
            if (isTodayWorking) {
                const openingTime = getTodayOpeningTimeIfEarly(now);
                if (openingTime) {
                    return openingTime;
                }
            }

            // Find next working day in current week
            const nextWorkingDay = getNextWorkingDayInWeek(now);
            if (nextWorkingDay) {
                return getFirstWorkingHourOfDay(nextWorkingDay);
            }

            // Fallback: first working day of week
            const firstWorkingDay = getFirstWorkingDayOfWeek(week.from);
            return getFirstWorkingHourOfDay(firstWorkingDay);
        }

        return calendarToday;
    };

    // Daily View Staff Ordering by Availability
    const staffResourceSorted: Array<StaffResource> = useMemo(() => {
        // Only apply special sorting for daily view
        if (viewMode !== CalendarView.Daily) {
            return staffResource;
        }

        const selectedDayName = date.format('dddd').toLowerCase() as WeekDay;

        return [...staffResource].sort((staffA, staffB) => {
            const fullStaffA = calendarStaff.find(s => s.id === staffA.id);
            const fullStaffB = calendarStaff.find(s => s.id === staffB.id);

            if (!fullStaffA || !fullStaffB) return 0;

            // Check if staff A is working on selected date
            const customA = fullStaffA.schedule.customDays.find(day => day.from.isSame(date, 'day'));
            const isWorkingA = customA ? customA.on : !!fullStaffA.schedule.default[selectedDayName];

            // Check if staff B is working on selected date
            const customB = fullStaffB.schedule.customDays.find(day => day.from.isSame(date, 'day'));
            const isWorkingB = customB ? customB.on : !!fullStaffB.schedule.default[selectedDayName];

            // Available staff (working) comes first
            if (isWorkingA && !isWorkingB) return -1;

            // Non-working staff goes to the end
            if (!isWorkingA && isWorkingB) return 1;

            // Within same availability group, sort alphabetically
            const nameA = `${staffA.firstName} ${staffA.lastName}`.toLowerCase();
            const nameB = `${staffB.firstName} ${staffB.lastName}`.toLowerCase();
            return nameA.localeCompare(nameB);
        });
    }, [staffResource, calendarStaff, date, viewMode]);

    return (
        <>
            <Box className={classes.container}>
                <CalendarHeader
                    businessArea={businessArea}
                    onChangeBusinessArea={businessAreaChangeHandler}
                    //
                    viewMode={viewMode}
                    onChangeViewMode={setViewMode}
                    //
                    week={week}
                    date={date}
                    month={month}
                    //
                    selectedStaffIds={selectedStaffIds}
                    onChangeStaffIds={staffIdsChangeHandler}
                    //
                    selectedLocationId={selectedLocationId}
                    onChangeLocationId={changeLocationIdHandler}
                    timeZone={calendarTimeZone}
                    //
                    selectedRoomUnitIds={selectedRoomUnitIds}
                    onChangeRoomUnitIds={roomUnitIdsChangeHandler}
                    //
                    isSlotsView={isSlotsView}
                    readOnly={readOnly}
                    onToday={handlerChangeToday}
                    onChangeMonth={monthChangeHandler}
                    onChangeWeek={weekChangeHandler}
                    onChangeDate={dateChangeHandler}
                    onChangeSlotsView={setIsSlotsView}
                    onAdd={() => bookHandler(getDefaultDateTimeForView())}
                />

                <Box className={classes.body}>
                    {loading.appointments || loading.blocks || loading.staff || loading.rooms ? (
                        <Spinner className={classes.spinnerStyle} />
                    ) : businessArea !== SERVICES_BUSINESS_AREAS.BOARDING ? (
                        <>
                            {viewMode === CalendarView.Monthly && (
                                <MonthFullCalendar
                                    month={month}
                                    timeZone={calendarTimeZone}
                                    schedule={marketplace?.schedule}
                                    showPetsCountBadge={showPetsCountBadge}
                                    staffs={calendarStaff}
                                    dailyCounts={appointmentDailyCounts}
                                    selectedStaffIds={selectedStaffIds}
                                    businessArea={businessArea}
                                    onBook={monthDayClickHandler}
                                />
                            )}

                            {viewMode === CalendarView.Weekly && (
                                <WeekFullCalendar
                                    from={week.from}
                                    timeZone={calendarTimeZone}
                                    schedule={marketplace?.schedule}
                                    showPetsCountBadge={showPetsCountBadge}
                                    staffs={calendarStaff}
                                    appointments={calendarAppointments}
                                    blocks={calendarBlocks}
                                    multipleBookingsIds={multipleBookingIds}
                                    tempNewBooking={tempNewBooking}
                                    selectedStaffIds={selectedStaffIds}
                                    shrink={newBooking !== undefined}
                                    isSlotsView={isSlotsView}
                                    businessArea={businessArea}
                                    readOnly={readOnly}
                                    onBook={bookHandler}
                                    onClickAppointment={showOrderHandler}
                                    onClickBlockCalendar={showBlockCalendarHandler}
                                    onRescheduled={rescheduleHandler}
                                    onRescheduledBlocked={rescheduleBlockedHandler}
                                    onScroll={handleCalendarScroll}
                                    onClickSlotGroup={(group, slotElement) => setEditSlotGroup({ group, slotElement })}
                                />
                            )}

                            {viewMode === CalendarView.Daily && (
                                <DayFullCalendar
                                    date={date}
                                    timeZone={calendarTimeZone}
                                    showPetsCountBadge={showPetsCountBadge}
                                    appointments={calendarAppointments}
                                    blocks={calendarBlocks}
                                    multipleBookingsIds={multipleBookingIds}
                                    staff={staffResourceSorted}
                                    tempNewBooking={tempNewBooking}
                                    loadingAppointmentIds={reschedulingAppointments.map(reschedule => reschedule.id)}
                                    isSlotsView={isSlotsView}
                                    businessArea={businessArea}
                                    readOnly={readOnly}
                                    onBook={bookHandler}
                                    onClickAppointment={showOrderHandler}
                                    onClickBlockCalendar={showBlockCalendarHandler}
                                    onClickStaffSlot={(slot, staffId, element) =>
                                        setEditSlot({ slot, staffId, slotElement: element })
                                    }
                                    onRescheduled={rescheduleHandler}
                                    onRescheduledBlocked={rescheduleBlockedHandler}
                                    onScroll={handleCalendarScroll}
                                />
                            )}
                        </>
                    ) : businessArea === SERVICES_BUSINESS_AREAS.BOARDING ? (
                        <BoardingWeekFullCalendar
                            date={week.from}
                            timeZone={calendarTimeZone}
                            selectedRoomUnitIds={selectedRoomUnitIds}
                            readOnly={readOnly}
                            onClickAppointment={showOrderHandler}
                            onBook={bookHandler}
                        />
                    ) : null}
                </Box>
            </Box>

            {(showNewClient || showClient) && newClient}

            {orderData.show && orderPanelView === 'booking_detail' && orderDetailView}

            {orderData.show && orderPanelView === 'activity_log' && orderData.bookingId && (
                <BookingActivityLog
                    bookingId={orderData.bookingId}
                    onClose={() => setOrderPanelView('booking_detail')}
                    onPanelClose={closeOrderHandler}
                />
            )}

            {rescheduleFromDetail && rescheduleBookingView}

            <BlockCalendarsWebSockets />

            {(newBooking || showBlockCalendar) && !showNewClient && createBooking}

            {reschedule && !recurring && (
                <ConfirmRescheduleDialog
                    name={reschedule.customerName}
                    from={reschedule.from}
                    to={reschedule.to}
                    open={true}
                    onCancel={cancelRescheduleHandler}
                    onConfirm={confirmRescheduleHandler}
                />
            )}

            <AppointmentEditScopeDialogContainer origin="calendar" />

            {rebookBooking && reBook}

            {reschedule && recurring && (
                <ConfirmRescheduleRecurringDialog
                    rescheduleRecurring
                    name={reschedule.customerName}
                    from={reschedule.from}
                    to={reschedule.to}
                    open={true}
                    onCancel={cancelRescheduleHandler}
                    onConfirm={confirmRescheduleHandler}
                />
            )}

            {showUpdateConfirmation && updateConfirmation}

            {checkoutData && checkout}

            {editSlot && (
                <EditSingleSlotFullCalendar
                    date={date}
                    slot={editSlot.slot}
                    staff={calendarStaff.find(staff => staff.id === editSlot.staffId)}
                    slotElement={editSlot.slotElement}
                    onClose={() => setEditSlot(undefined)}
                />
            )}

            {editSlotGroup && (
                <EditGroupSlotFullCalendar
                    slotGroup={editSlotGroup.group}
                    slotElement={editSlotGroup.slotElement}
                    onClose={() => setEditSlotGroup(undefined)}
                />
            )}
        </>
    );
};

export default Calendar;
